import { Component } from 'react';
import classNames from 'classnames';
import { now } from '../lib/now';
import classes from './clamp.less';

type Props = {
  className?: string;
  text: string;
  timeLimit: number; // in milliseconds
  windowResize: boolean | null;
};

const specialChars = ',.!?';

export default class Clamp extends Component<Props> {
  static defaultProps = {
    text: '',
    timeLimit: 10,
    windowResize: null,
  };

  componentDidMount() {
    this.clamp();
  }

  componentDidUpdate(prevProps: Props) {
    if (
      prevProps.text !== this.props.text ||
      prevProps.windowResize !== this.props.windowResize
    ) {
      this.clamp();
    }
  }

  _containerRef: HTMLDivElement | null = null;
  _clampingTextRef: HTMLDivElement | null = null;

  setContainerRef = (ref: HTMLDivElement | null) => {
    this._containerRef = ref;
  };

  setClampingTextRef = (ref: HTMLDivElement | null) => {
    this._clampingTextRef = ref;
  };

  getContainerHeight(): number {
    if (this._containerRef) {
      return this._containerRef.getBoundingClientRect().height;
    }
    return 0;
  }

  getClampTextHeight(): number {
    if (this._clampingTextRef) {
      return this._clampingTextRef.getBoundingClientRect().height;
    }
    return 0;
  }

  measure = (text: string): number => {
    if (this._clampingTextRef) {
      this._clampingTextRef.innerText = text;
      return this.getClampTextHeight();
    }
    return 0;
  };

  clamp() {
    const testText = this.props.text.trim();
    let testHeight = this.measure(testText);
    const maxHeight = this.getContainerHeight();

    if (testHeight <= maxHeight) {
      return;
    }

    // the word at "mid" index is quite close to the cutoff word
    const words = testText.split(' ');
    let mid = Math.ceil((words.length * maxHeight) / testHeight) + 1;

    const cutAt = (index: number): string => {
      const text = words.slice(0, index).join(' ');

      // remove special characters at trailing
      let i = text.length - 1;
      for (; i >= 0; i--) {
        if (specialChars.indexOf(text[i]) === -1) {
          break;
        }
      }

      return `${text.substring(0, i + 1)} ...`;
    };

    testHeight = this.measure(cutAt(mid));
    const timeLimit = this.props.timeLimit;
    const start = now();
    while (testHeight < maxHeight && now() - start < timeLimit) {
      mid += 1;
      testHeight = this.measure(cutAt(mid));
    }

    while (testHeight > maxHeight && now() - start < timeLimit) {
      mid -= 1;
      testHeight = this.measure(cutAt(mid));
    }
  }

  render() {
    const { className, text } = this.props;
    return (
      <div
        className={classNames(className, classes.clampContainer)}
        ref={this.setContainerRef}
        title={text}
      >
        <div ref={this.setClampingTextRef} />
      </div>
    );
  }
}
